import { Trophy, User, Check } from "lucide-react";

export default function TournamentBracket({ matches, tournamentType, onMatchClick }) {
  // Separate matches by bracket type
  const winnersMatches = matches.filter(m => m.bracket === "winners");
  const losersMatches = matches.filter(m => m.bracket === "losers");
  const grandFinals = matches.filter(m => m.bracket === "grand_finals");

  // Group by rounds
  const groupByRound = (matchList) => {
    const rounds = {};
    matchList.forEach(match => {
      if (!rounds[match.round]) rounds[match.round] = [];
      rounds[match.round].push(match);
    });
    return rounds;
  };

  const winnersRounds = groupByRound(winnersMatches);
  const losersRounds = groupByRound(losersMatches);
  const roundCount = Object.keys(winnersRounds).length;

  const getRoundName = (roundNum, totalRounds, bracket) => {
    if (bracket === "grand_finals") return "Büyük Final";
    if (bracket === "losers") return `Kaybeden ${roundNum}`;
    
    if (roundNum === totalRounds) return "Final";
    if (roundNum === totalRounds - 1) return "Yarı Final";
    if (roundNum === totalRounds - 2) return "Çeyrek Final";
    return `Tur ${roundNum}`;
  };

  const MatchCard = ({ match, onClick }) => {
    const isClickable = match.status === "pending" && match.player1 && match.player2;
    const isCompleted = match.status === "completed";
    const isWaiting = match.status === "waiting" || !match.player1 || !match.player2;

    return (
      <div
        onClick={() => isClickable && onClick?.(match)}
        className={`rounded-lg border overflow-hidden transition-all ${
          isClickable 
            ? "border-[#4DFFFF]/50 cursor-pointer hover:border-[#4DFFFF] hover:bg-[#4DFFFF]/5" 
            : isCompleted
              ? "border-[#C6A87C]/30"
              : "border-white/10 opacity-60"
        }`}
        data-testid={`match-${match.match_id}`}
      >
        {/* Player 1 */}
        <div className={`flex items-center justify-between px-3 py-2 ${
          isCompleted && match.winner_id === match.player1?.user_id
            ? "bg-[#C6A87C]/20"
            : "bg-white/5"
        }`}>
          <div className="flex items-center gap-2">
            {match.player1 ? (
              <>
                <div className="w-6 h-6 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#4DFFFF]">
                    {match.player1.name?.charAt(0)}
                  </span>
                </div>
                <span className={`text-sm ${
                  match.player1.user_id === "BYE" ? "text-[#64748B] italic" : "text-white"
                }`}>
                  {match.player1.name}
                </span>
              </>
            ) : (
              <span className="text-sm text-[#64748B] italic">TBD</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCompleted && match.score_player1 !== null && (
              <span className="text-sm text-[#B1C1C1]">{match.score_player1}</span>
            )}
            {isCompleted && match.winner_id === match.player1?.user_id && (
              <Check className="w-4 h-4 text-[#C6A87C]" />
            )}
          </div>
        </div>
        
        {/* Divider */}
        <div className="h-px bg-white/10" />
        
        {/* Player 2 */}
        <div className={`flex items-center justify-between px-3 py-2 ${
          isCompleted && match.winner_id === match.player2?.user_id
            ? "bg-[#C6A87C]/20"
            : "bg-white/5"
        }`}>
          <div className="flex items-center gap-2">
            {match.player2 ? (
              <>
                <div className="w-6 h-6 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#4DFFFF]">
                    {match.player2.name?.charAt(0)}
                  </span>
                </div>
                <span className={`text-sm ${
                  match.player2.user_id === "BYE" ? "text-[#64748B] italic" : "text-white"
                }`}>
                  {match.player2.name}
                </span>
              </>
            ) : (
              <span className="text-sm text-[#64748B] italic">TBD</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCompleted && match.score_player2 !== null && (
              <span className="text-sm text-[#B1C1C1]">{match.score_player2}</span>
            )}
            {isCompleted && match.winner_id === match.player2?.user_id && (
              <Check className="w-4 h-4 text-[#C6A87C]" />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (matches.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
        <p className="text-[#B1C1C1]">Henüz maç yok</p>
      </div>
    );
  }

  return (
    <div className="tournament-bracket">
      {/* Winners Bracket */}
      <div className="mb-8">
        <h3 className="text-sm font-bold text-[#C6A87C] uppercase tracking-wider mb-4">
          {tournamentType === "double_elimination" ? "Kazananlar Şeması" : "Turnuva Şeması"}
        </h3>
        
        <div className="flex gap-8 overflow-x-auto pb-4">
          {Object.entries(winnersRounds).map(([roundNum, roundMatches]) => (
            <div key={roundNum} className="flex-shrink-0">
              <p className="text-xs text-[#64748B] uppercase tracking-wider mb-3 text-center">
                {getRoundName(parseInt(roundNum), roundCount, "winners")}
              </p>
              <div className="flex flex-col gap-4 justify-around" style={{ 
                minHeight: roundMatches.length > 1 ? `${roundMatches.length * 100}px` : 'auto'
              }}>
                {roundMatches.map((match) => (
                  <div key={match.match_id} className="w-48">
                    <MatchCard match={match} onClick={onMatchClick} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Losers Bracket (Double Elimination only) */}
      {tournamentType === "double_elimination" && Object.keys(losersRounds).length > 0 && (
        <div className="mb-8 pt-6 border-t border-white/10">
          <h3 className="text-sm font-bold text-red-400 uppercase tracking-wider mb-4">
            Kaybedenler Şeması
          </h3>
          
          <div className="flex gap-8 overflow-x-auto pb-4">
            {Object.entries(losersRounds).map(([roundNum, roundMatches]) => (
              <div key={roundNum} className="flex-shrink-0">
                <p className="text-xs text-[#64748B] uppercase tracking-wider mb-3 text-center">
                  {getRoundName(parseInt(roundNum), Object.keys(losersRounds).length, "losers")}
                </p>
                <div className="flex flex-col gap-4 justify-around">
                  {roundMatches.map((match) => (
                    <div key={match.match_id} className="w-48">
                      <MatchCard match={match} onClick={onMatchClick} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grand Finals (Double Elimination only) */}
      {grandFinals.length > 0 && (
        <div className="pt-6 border-t border-white/10">
          <h3 className="text-sm font-bold text-[#C6A87C] uppercase tracking-wider mb-4">
            Büyük Final
          </h3>
          <div className="w-48">
            <MatchCard match={grandFinals[0]} onClick={onMatchClick} />
          </div>
        </div>
      )}

      {/* Click instruction */}
      <p className="text-xs text-[#64748B] mt-6 text-center">
        Bekleyen maçlara tıklayarak sonuç girebilirsiniz
      </p>
    </div>
  );
}
